import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { bdDashboardAPI } from "../../api";
import {
  anonymizeRows,
  downloadCsvFile,
  formatDateTime,
  formatValue,
} from "./wplDashboardHelpers";
import "./wpl-dashboard.css";

const ACCESS_COLUMNS = [
  { key: "windfarm_name", label: "Windfarm" },
  { key: "customer", label: "Customer" },
  { key: "owner", label: "Owner" },
  { key: "firstname", label: "First name" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
  { key: "last_login_at", label: "Last login", format: formatDateTime },
  { key: "access_created_at", label: "Access since", format: formatDateTime },
  { key: "subscription_status", label: "Subscription" },
  { key: "subscription_price", label: "Price" },
  { key: "yearly_revenue", label: "Yearly revenue" },
  { key: "subscription_note", label: "Note" },
  { key: "recommendation", label: "Recommendation" },
];

const ACCESS_MODES = [
  { value: "windfarm", label: "By windfarm id / name", placeholder: "Windfarm id or name" },
  { value: "user", label: "By user id / email", placeholder: "User id or email" },
  { value: "customer", label: "By customer / owner", placeholder: "Customer or owner" },
  { value: "review", label: "Review candidates", placeholder: "" },
];

const buildAccessParams = (mode, value, excludePresales) => {
  const trimmed = (value || "").trim();
  if (mode === "windfarm") {
    return /^\d+$/.test(trimmed)
      ? { windfarm_id: trimmed }
      : { windfarm_name: trimmed };
  }
  if (mode === "user") {
    // A bare number is a user id; anything else (name or partial email) is a
    // text search across email + first name.
    return /^\d+$/.test(trimmed)
      ? { target_user_id: trimmed }
      : { email: trimmed };
  }
  if (mode === "customer") return { customer: trimmed };
  if (mode === "review") {
    const params = { view: "review-candidates" };
    if (excludePresales) params.exclude_presales = true;
    return params;
  }
  return {};
};

const cellValue = (row, column) =>
  column.format ? column.format(row[column.key]) : formatValue(row[column.key]);

function AccessSubscriptionsSection() {
  const [mode, setMode] = useState("customer");
  const [inputValue, setInputValue] = useState("");
  const [excludePresales, setExcludePresales] = useState(true);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const isReview = mode === "review";

  const runSearch = (event) => {
    if (event) event.preventDefault();
    if (!isReview && inputValue.trim() === "") return;

    setLoading(true);
    setError(null);
    bdDashboardAPI
      .accessLookup(buildAccessParams(mode, inputValue, excludePresales))
      .then((data) => {
        setRows(Array.isArray(data?.rows) ? anonymizeRows(data.rows) : []);
        setCounts(data?.counts ?? null);
        setHasSearched(true);
      })
      .catch((err) => {
        setError(err);
        setRows([]);
        setCounts(null);
        setHasSearched(true);
      })
      .finally(() => setLoading(false));
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const csvRows = [
      ACCESS_COLUMNS.map((column) => column.label),
      ...rows.map((row) => ACCESS_COLUMNS.map((column) => cellValue(row, column))),
    ];
    downloadCsvFile("access-subscriptions.csv", csvRows);
  };

  return (
    <section className="bd-section bd-access-section">
      <h2>Access &amp; Subscriptions</h2>
      <form className="bd-access-filter-bar" onSubmit={runSearch}>
        <select
          className="bd-access-mode"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          {ACCESS_MODES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {!isReview && (
          <input
            className="bd-access-input"
            type="text"
            placeholder={
              ACCESS_MODES.find((option) => option.value === mode)?.placeholder
            }
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        )}
        {isReview && (
          <label className="bd-access-checkbox">
            <input
              type="checkbox"
              checked={excludePresales}
              onChange={(e) => setExcludePresales(e.target.checked)}
            />
            Exclude presales access
          </label>
        )}
        <button className="bd-access-search-button" type="submit">
          Search
        </button>
        {rows.length > 0 && (
          <button
            className="bd-download-csv-button"
            type="button"
            onClick={exportCsv}
          >
            Download CSV
          </button>
        )}
      </form>

      {counts && (
        <div className="bd-access-counts">
          <span className="bd-access-chip">{counts.total} total accesses</span>
          <span className="bd-access-chip bd-access-chip-review">
            {counts.to_review} to review
          </span>
          <span className="bd-access-chip bd-access-chip-paid">
            {counts.paid} paid
          </span>
          <span className="bd-access-chip">{counts.unpaid} unpaid</span>
        </div>
      )}

      {error && (
        <p className="bd-error">Could not load access data. Please try again.</p>
      )}

      {loading ? (
        <div className="bd-section-skeleton">
          <div className="bd-skeleton-chart" />
        </div>
      ) : rows.length > 0 ? (
        <div className="bd-table-wrapper">
          <table className="bd-table bd-access-table">
            <thead>
              <tr>
                {ACCESS_COLUMNS.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.access_id}
                  className={row.discrepancy ? "bd-access-row-discrepancy" : ""}
                >
                  {ACCESS_COLUMNS.map((column) => (
                    <td key={column.key}>{cellValue(row, column)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        hasSearched && <p className="bd-empty">No matching access rows.</p>
      )}
    </section>
  );
}

const LEAD_WINDOW_OPTIONS = [
  { value: 14, label: "Last 2 weeks" },
  { value: 30, label: "Last month" },
  { value: 90, label: "Last 3 months" },
];

const probabilityLabel = (prob) => {
  if (prob === null || prob === undefined) return { text: "—", level: "none" };
  if (prob < 0.09) return { text: "Low", level: "low" };
  if (prob < 0.37) return { text: "Medium", level: "medium" };
  return { text: "High", level: "high" };
};

const formatPercent = (prob) => {
  if (prob === null || prob === undefined) return "—";
  return `${Math.round(prob * 100)}%`;
};

function LeadDetailModal({ lead, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!lead) return null;

  return (
    <div className="bd-modal-overlay" onClick={onClose}>
      <div
        className="bd-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bd-modal-header">
          <div>
            <h3 className="bd-modal-title">{lead.windfarm_name}</h3>
            <p className="bd-lead-subtitle">
              {lead.owner || lead.customer || "Unknown owner"}
            </p>
          </div>
          <button
            type="button"
            className="bd-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="bd-access-counts">
          <span className="bd-access-chip">
            {lead.turbine_count} turbines affected
          </span>
          <span className="bd-access-chip">
            {lead.total_strikes} lightning strikes
          </span>
          <span className="bd-access-chip bd-access-chip-review">
            {lead.total_outside_iec} outside IEC
          </span>
        </div>

        <div className="bd-modal-body">
          <table className="bd-lead-turbines">
            <thead>
              <tr>
                <th>Turbine</th>
                <th>Lightning strikes</th>
                <th>Outside IEC</th>
                <th>Subsequent stroke</th>
                <th>Probability</th>
                <th>Last strike</th>
              </tr>
            </thead>
            <tbody>
              {lead.turbines.map((turbine) => {
                const prob = probabilityLabel(turbine.probability);
                return (
                  <tr
                    key={turbine.turbine_id}
                    className={
                      turbine.outside_iec_count > 0
                        ? "bd-lead-turbine-iec"
                        : undefined
                    }
                  >
                    <td>{turbine.turbine_name}</td>
                    <td>{turbine.strike_count}</td>
                    <td>{turbine.outside_iec_count || "—"}</td>
                    <td>{turbine.subsequent_stroke_count || "—"}</td>
                    <td>
                      <span className={`bd-prob-badge bd-prob-${prob.level}`}>
                        {prob.text}
                      </span>{" "}
                      {formatPercent(turbine.probability)}
                    </td>
                    <td>{formatDateTime(turbine.last_strike_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LightningLeadsSection() {
  const [days, setDays] = useState(14);
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    bdDashboardAPI
      .lightningLeads({ days })
      .then((data) => {
        if (!isMounted) return;
        setLeads(Array.isArray(data?.leads) ? anonymizeRows(data.leads) : []);
        setCounts(data?.counts ?? null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setLeads([]);
        setCounts(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [days]);

  return (
    <section className="bd-section bd-leads-section">
      <div className="bd-leads-header">
        <h2>Sales Leads — Lightning Activity</h2>
        <select
          className="bd-access-mode"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {LEAD_WINDOW_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {counts && (
        <div className="bd-access-counts">
          <span className="bd-access-chip">{counts.windfarms} windfarms</span>
          <span className="bd-access-chip">
            {counts.turbines_affected} turbines affected
          </span>
          <span className="bd-access-chip bd-access-chip-review">
            {counts.outside_iec} outside IEC
          </span>
        </div>
      )}

      {error && (
        <p className="bd-error">Could not load lightning leads. Please try again.</p>
      )}
      {loading && <p className="bd-empty">Loading…</p>}

      {!loading && leads.length === 0 && !error && (
        <p className="bd-empty">
          No lightning activity on non-subscribed windfarms in this window.
        </p>
      )}

      {leads.length > 0 && (
        <div className="bd-leads-grid">
          {leads.map((lead) => {
            const maxProb = probabilityLabel(lead.max_probability);
            return (
              <button
                type="button"
                key={lead.windfarm_id}
                className="bd-lead-card"
                onClick={() => setSelectedLead(lead)}
              >
                <div className="bd-lead-card-header">
                  <h3 className="bd-lead-title">{lead.windfarm_name}</h3>
                  <p className="bd-lead-subtitle">
                    {lead.owner || lead.customer || "Unknown owner"}
                  </p>
                </div>
                <div className="bd-lead-metrics">
                  <div className="bd-lead-metric">
                    <span className="bd-lead-metric-value">
                      {lead.turbine_count}
                    </span>
                    <span className="bd-lead-metric-label">Turbines affected</span>
                  </div>
                  <div className="bd-lead-metric">
                    <span className="bd-lead-metric-value">
                      {lead.total_strikes}
                    </span>
                    <span className="bd-lead-metric-label">Lightning strikes</span>
                  </div>
                  <div className="bd-lead-metric">
                    <span
                      className={`bd-lead-metric-value${
                        lead.total_outside_iec > 0 ? " bd-lead-metric-iec" : ""
                      }`}
                    >
                      {lead.total_outside_iec}
                    </span>
                    <span className="bd-lead-metric-label">Outside IEC</span>
                  </div>
                  <div className="bd-lead-metric">
                    <span className={`bd-prob-badge bd-prob-${maxProb.level}`}>
                      {maxProb.text}
                    </span>
                    <span className="bd-lead-metric-label">
                      Probability of attachment
                    </span>
                  </div>
                </div>
                <p className="bd-lead-footer">
                  Last notification:{" "}
                  {lead.last_notification_at
                    ? formatDateTime(lead.last_notification_at)
                    : "No notifications sent"}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </section>
  );
}

export default function BdLassiePage() {
  return (
    <div className="bd-dashboard">
      <div className="bd-page-header">
        <h1>Lassie — BD</h1>
        <Link className="bd-page-link" to="/internal/wpl-dashboard">
          ← WPL Dashboard
        </Link>
      </div>
      <AccessSubscriptionsSection />
      <LightningLeadsSection />
    </div>
  );
}
